import { useState } from 'react';
import { FaExclamationTriangle } from 'react-icons/fa';
import { getLocalDateString } from '../../utils/dateUtils';

const CompletionModal = ({
  isFixedFee,
  completionDate,
  setCompletionDate,
  saving,
  onCancel,
  onConfirm,
}) => {
  const [dateError, setDateError] = useState('');

  // Get today's date in YYYY-MM-DD format for max attribute
  const getTodayDateString = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setCompletionDate(newDate);
    
    // Validate date is not in the future
    if (newDate) {
      // Parse local YYYY-MM-DD
      const [year, month, day] = newDate.split('-').map(Number);
      const selectedDate = new Date(year, month - 1, day);
      const today = new Date();
      today.setHours(23, 59, 59, 999); // End of today
      
      if (selectedDate > today) {
        setDateError('Completion date cannot be in the future. Please select today or an earlier date.');
      } else {
        setDateError('');
      }
    } else {
      setDateError('');
    }
  };

  const handleConfirm = () => {
    // Final validation before confirming
    if (completionDate) {
      const [year, month, day] = completionDate.split('-').map(Number);
      const selectedDate = new Date(year, month - 1, day);
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      
      if (selectedDate > today) {
        setDateError('Completion date cannot be in the future. Please select today or an earlier date.');
        return;
      }
    }
    
    if (!completionDate) {
      setDateError('Please select a completion date.');
      return;
    }
    
    setDateError('');
    onConfirm();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md">
        <h4 className="text-lg font-semibold mb-4 text-gray-800">Confirm Job Completion</h4>
        {!isFixedFee && (
          <p className="text-sm text-yellow-700 bg-yellow-100 p-3 rounded mb-4 border border-yellow-300">
            <FaExclamationTriangle className="inline mr-1" />
            Once marked complete, your Actual Hours cannot be changed. To fulfill your resource allocation, set or leave status as APPROVED and save today's hours.
          </p>
        )}
        <div className="mb-4">
          <label htmlFor="completionDate" className="block text-sm font-medium text-gray-700 mb-1">
            Completion Date
          </label>
          <input
            type="date"
            id="completionDate"
            value={completionDate}
            onChange={handleDateChange}
            max={getTodayDateString()}
            className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 sm:text-sm ${
              dateError 
                ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                : 'border-gray-300 focus:ring-green-500 focus:border-green-500'
            }`}
          />
          {dateError && (
            <p className="mt-2 text-sm text-red-600 flex items-center">
              <FaExclamationTriangle className="mr-1 flex-shrink-0" />
              {dateError}
            </p>
          )}
        </div>
        <div className="flex justify-end space-x-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-full shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-full text-white bg-green-600 hover:bg-green-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
            disabled={saving || !completionDate || dateError}
          >
            {saving ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </>
            ) : (
              'Confirm Completion'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompletionModal;
